import React, { useState } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import { Button } from '@/components/ui/button';
import {
  ClientSelector,
  ProjectSelector,
  LayoutSelector,
  ScanDetailsInputs,
  FileUpload,
  UploadProgress,
  AlertMessage,
} from '../components';
import { useClients, useProjects, useLayouts } from '../hooks';
import { getSignedUrls, createScan, uploadFilesToGCS } from '../api/scan-api';
import { ScanFormData } from '../types';

export const AddScanForm: React.FC = () => {
  const { user } = useUser();

  // Form state
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedLayout, setSelectedLayout] = useState<string>('');
  const [scanDate, setScanDate] = useState<string>('');
  const [scanType, setScanType] = useState<string>('');
  const [uploadType, setUploadType] = useState<string>('');
  const [scanNumber, setScanNumber] = useState<string>('');
  const [scanArea, setScanArea] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // UI state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data hooks
  const { clients } = useClients(user?.is_staff || false);
  const { projects, isLoading: isLoadingProjects } = useProjects(selectedClient);
  const { layouts, isLoading: isLoadingLayouts } = useLayouts(selectedProject, scanType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!selectedClient || !selectedProject || !scanDate || !scanType || 
        !uploadType || !scanNumber || !scanArea) {
      setError('Please fill in all required fields');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select files to upload');
      return;
    }

    if (scanType !== 'Progress' && !selectedLayout) {
      setError('Please select a layout for non-Progress scans');
      return;
    }

    try {
      setIsUploading(true);
      setUploadStatus('Preparing upload...');
      setUploadProgress(0);

      // Get the layout name if needed
      let layoutName: string | undefined;
      if (scanType !== 'Progress' && selectedLayout) {
        const layout = layouts.find(l => l.id === parseInt(selectedLayout));
        layoutName = layout?.name;
      }

      // Step 1: Get signed URLs
      setUploadStatus('Getting upload URLs...');
      const fileNames = selectedFiles.map(f => f.name);
      const signedUrls = await getSignedUrls(
        fileNames,
        scanType,
        parseInt(selectedProject),
        scanDate,
        layoutName
      );

      // Step 2: Upload files to GCS
      setUploadStatus('Uploading files...');
      const uploadedUrls = await uploadFilesToGCS(
        selectedFiles,
        signedUrls,
        (overall) => {
          if (overall >= 0) {
            setUploadProgress(overall);
            setUploadStatus(`Uploading files: ${Math.floor(overall)}%`);
          }
        }
      );

      // Step 3: Create scan record
      setUploadStatus('Creating scan record...');
      setUploadProgress(100);

      const scanData: ScanFormData = {
        client: parseInt(selectedClient),
        project: parseInt(selectedProject),
        scan_date: scanDate,
        scan_type: scanType as 'Progress' | 'Asbuild' | 'Corridor',
        upload_type: uploadType as 'Full' | 'Partial',
        scan_number: parseInt(scanNumber),
        scan_area: scanArea,
        layout_ids: selectedLayout ? [parseInt(selectedLayout)] : undefined
      };

      const result = await createScan(scanData, fileNames, uploadedUrls);

      if (result.success) {
        setSuccess(result.message || 'Scan created successfully!');
        
        // Clear only the uploaded files and progress indicators
        // Keep all form fields so user can easily upload another scan with similar parameters
        setSelectedFiles([]);
        setUploadProgress(0);
        setUploadStatus('');
      } else {
        setError(result.errors || 'Failed to create scan');
      }
    } catch (err: any) {
      console.error('Error creating scan:', err);
      setError(err.response?.data?.error || err.message || 'Failed to create scan');
    } finally {
      setIsUploading(false);
    }
  };

  // Check if user is authenticated and has permission
  if (!user) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h1 className="text-3xl font-bold mb-6">Add Scan</h1>
        <AlertMessage 
          type="error" 
          message="You must be logged in to access this page. Please log in and try again." 
        />
      </div>
    );
  }

  if (!user.is_staff) {
    return (
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h1 className="text-3xl font-bold mb-6">Add Scan</h1>
        <AlertMessage 
          type="error" 
          message="You must be a staff member to add scans. Please contact your administrator for access." 
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
      <h1 className="text-3xl font-bold mb-6">Add Scan</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <ClientSelector
          clients={clients}
          value={selectedClient}
          onChange={setSelectedClient}
        />

        <ProjectSelector
          projects={projects}
          value={selectedProject}
          onChange={setSelectedProject}
          disabled={!selectedClient}
          isLoading={isLoadingProjects}
        />

        <ScanDetailsInputs
          scanDate={scanDate}
          scanType={scanType}
          uploadType={uploadType}
          scanNumber={scanNumber}
          scanArea={scanArea}
          onScanDateChange={setScanDate}
          onScanTypeChange={setScanType}
          onUploadTypeChange={setUploadType}
          onScanNumberChange={setScanNumber}
          onScanAreaChange={setScanArea}
        />

        {scanType && scanType !== 'Progress' && (
          <LayoutSelector
            layouts={layouts}
            value={selectedLayout}
            onChange={setSelectedLayout}
            disabled={!selectedProject || !scanType}
            isLoading={isLoadingLayouts}
          />
        )}

        <FileUpload
          selectedFiles={selectedFiles}
          onChange={setSelectedFiles}
          disabled={isUploading}
        />

        {isUploading && (
          <UploadProgress
            progress={uploadProgress}
            status={uploadStatus}
          />
        )}

        <Button
          type="submit"
          disabled={isUploading}
          className="w-full"
        >
          {isUploading ? 'Uploading...' : 'Submit Scan'}
        </Button>

        {error && <AlertMessage type="error" message={error} />}
        {success && <AlertMessage type="success" message={success} />}
      </form>
    </div>
  );
};

